import type { SocialConnection } from '@pulsify/shared/dto'
import { Loader2 } from 'lucide-react'
import { ConnectHelpButton } from '~/features/crossposting/components/crossposting-connect-help'
import { PLATFORM_UI } from '~/features/crossposting/lib/platforms'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'

interface SocialPlatformCardProps {
  connection: SocialConnection
  onConnect: () => void
  onDisconnect: () => void
  isConnecting: boolean
  isDisconnecting: boolean
}

export function SocialPlatformCard({
  connection,
  onConnect,
  onDisconnect,
  isConnecting,
  isDisconnecting,
}: SocialPlatformCardProps) {
  const ui = PLATFORM_UI[connection.platform]
  const Icon = ui.icon
  const isConnected = connection.status === 'connected'
  const isPending = connection.status === 'pending'
  const accountName = connection.displayName || connection.username

  return (
    <Card className="flex flex-col gap-4 p-6">
      <div className="flex items-center gap-4">
        <span
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg transition-colors',
            !isConnected && 'bg-muted',
          )}
          style={isConnected ? ui.brandStyle : undefined}
        >
          <Icon className={cn('h-6 w-6', !isConnected && 'text-muted-foreground/50')} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold tracking-tight">{ui.label()}</h3>
            {isConnected && <Badge variant="success">{m.crosspostingConnected()}</Badge>}
            {isPending && <Badge variant="warning">{m.crosspostingActionNeeded()}</Badge>}
          </div>
          <p className="line-clamp-1 text-muted-foreground text-sm">
            {isConnected && accountName
              ? m.crosspostingConnectedAs({ name: accountName })
              : isPending
                ? m.crosspostingPendingHint()
                : m.crosspostingNotConnected()}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {isConnected ? (
          <Button variant="outline" size="sm" onClick={onDisconnect} disabled={isDisconnecting}>
            {isDisconnecting && <Loader2 className="h-4 w-4 animate-spin" />}
            {m.crosspostingDisconnect()}
          </Button>
        ) : (
          <Button size="sm" onClick={onConnect} disabled={isConnecting}>
            {isConnecting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isPending ? m.crosspostingFinishConnecting() : m.crosspostingConnect()}
          </Button>
        )}
        <ConnectHelpButton platform={connection.platform} className="ml-auto" />
      </div>
    </Card>
  )
}
